import React from 'react';
import semver from 'semver';

import { APP_TYPES } from 'constants/app';

import { formatCurrencyMaybeRound } from 'utils';

import Status from 'components/status';
import Tooltip from 'components/tooltip';

const { CLIENT_URL } = process.env;

/**
 * Get app type label.
 * @param {string} id
 * @returns {string}
 */
export function getAppTypeLabel(id) {
  return APP_TYPES.find((type) => type.id === id)?.label || id;
}

/**
 * Render app type label.
 * @param {string} id
 * @param {boolean} renderTooltip
 * @returns {string}
 */
export function renderAppTypeLabel(id, renderTooltip = true) {
  const typeDef = APP_TYPES.find((type) => type.id === id);

  const labelElement = (
    <Status type="muted" dot={false}>
      {typeDef?.label || id}
    </Status>
  );

  return typeDef && renderTooltip ? (
    <Tooltip message={APP_TYPES.find((type) => type.id === id)?.description}>
      {labelElement}
    </Tooltip>
  ) : (
    labelElement
  );
}

/**
 * Get frontend URL for an installed app.
 * @param {object} installedApp
 * @returns {string}
 */
export function getFrontendURL(installedApp) {
  if (!installedApp) return '';

  const { parent_id, id } = installedApp;

  return CLIENT_URL.replace('CLIENT_ID', `${parent_id}--${id}--app`);
}

/**
 * Check if version1 is greater than version2.
 * @param {string} version1
 * @param {string} version2
 * @returns {boolean}
 */
export function isVersionGreater(version1, version2) {
  if (typeof version1 !== 'string' || typeof version2 !== 'string') {
    return false;
  }
  return semver.compare(version1, version2) > 0;
}

/**
 * Get new publishable version if available.
 * @param {object} app
 * @returns {string|undefined}
 */
export function getNewPublishableVersion(app) {
  const { publishable_versions, published_props } = app;

  const publishedVersion = published_props?.version;

  const publishableVersion = publishable_versions?.results?.[0]?.version;

  if (!publishableVersion) {
    return;
  }

  const newPublishableVersion =
    publishedVersion && isVersionGreater(publishableVersion, publishedVersion)
      ? publishableVersion
      : !publishedVersion && publishableVersion;

  return newPublishableVersion;
}

/**
 * Check if app update is available.
 * @param {object} app
 * @param {object} installedApp
 * @returns {string|null}
 */
export function appUpdateAvailable(app, installedApp) {
  if (!installedApp) {
    return null;
  }

  const isPrivate = app.client_id === installedApp.parent_id;
  const availableVersion = isPrivate
    ? app.version
    : app.published_props?.version;

  return installedApp.version &&
    isVersionGreater(availableVersion, installedApp.version)
    ? availableVersion
    : null;
}

/**
 * Check if app is owned by the client.
 * @param {object} app
 * @param {object} client
 * @returns {boolean}
 */
export function isOwnApp(app, client) {
  return app.client_id === client.id;
}

/**
 * @param {object} app
 * @returns {string}
 */
export function getAppSlugId(app) {
  return app.private_id.startsWith('_')
    ? app.private_id.slice(1)
    : app.private_id;
}

/**
 * Render app price.
 * @param {object} app
 * @param {object} client
 * @returns {React.ReactNode}
 */
export function renderAppPrice(app, client) {
  const { price } = app;
  return (
    <>
      {isOwnApp(app, client)
        ? ''
        : price > 0
        ? formatCurrencyMaybeRound(price, 'USD')
        : 'Free'}
    </>
  );
}

/**
 * Get app developer URL.
 * @param {object} app
 * @param {object} owner
 * @return {string}
 */
export function getAppDeveloperUrl(app, owner) {
  return app.support_url || owner.support_website || app.repository_url;
}